import { stopBubbling } from '@Utils/helpers/eventHelpers';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import IconButton from '@Components/IconButton/IconButton';
import IconStyles from '@Components/IconButton/IconButton.scss';
import NotificationsApi from '@Data/notifications/Api';
import {
    INotification,
    NotificationType,
    ProgressState,
} from '@Data/notifications/Interfaces';
import {
    hasUnreadNotificationsState,
    notificationsState,
} from '@Data/notifications/Reducer';
import ProcessesApi from '@Data/processes/Api';
import Styles from '../../Header.scss';
import NotificationIcon from './NotificationIcon';
import NotificationItem from './NotificationItem';
import ProcessNotificationItem from './ProcessNotificationItem';
import SequentialNotificationItem from './SequentialNotificationItem';

interface INotificationsComponentProps {
    isSubmitting: boolean;
}

const NotificationsComponent = (props: INotificationsComponentProps) => {
    const [open, setIsOpen] = useState<boolean>(false);
    const notificationBtnId = 'koru-notification-btn';
    const notifications: Array<INotification> = useSelector(notificationsState);
    const hasUnreadNotifications: boolean = useSelector(
        hasUnreadNotificationsState
    );

    const toggleMenu = (e) => {
        stopBubbling(e);
        setIsOpen(!open);
    };

    const handleCancelNotification = (notification: INotification) => {
        if (
            notification.type === NotificationType.PROGRESS &&
            notification.progressInfo.state === ProgressState.PENDING
        ) {
            NotificationsApi.cancelProgressNotification(notification);
            ProcessesApi.cancelProcess(notification.id);
        } else {
            handleRemoveNotification(notification.id);
        }
    };

    const handleRemoveNotification = (notificationId: string) => {
        if (notifications.length === 1) {
            setIsOpen(false);
        }
        NotificationsApi.removeNotification(notificationId);
    };

    useEffect(() => {
        NotificationsApi.setAllNotificationsRead();
    }, [open]);

    const notificationsHtml = useMemo(() => {
        if (!_isEmpty(notifications)) {
            return notifications.map((n) => {
                switch (n.type) {
                    case NotificationType.STATIC:
                        return (
                            <NotificationItem
                                key={n.id}
                                id={`static-notification-${n.id}`}
                                severity={n.severity}
                                message={n.message}
                                onCancel={() => handleCancelNotification(n)}
                            />
                        );
                    case NotificationType.PROGRESS:
                        return (
                            <ProcessNotificationItem
                                key={n.id}
                                id={`progress-notification-${n.id}`}
                                notification={n}
                                onCancel={() => handleCancelNotification(n)}
                            />
                        );
                    case NotificationType.SEQUENTIAL:
                        return (
                            <SequentialNotificationItem
                                key={n.id}
                                id={`sequential-notification-${n.id}`}
                                remaining={n.sequentialInfo.remaining}
                                total={n.sequentialInfo.total}
                                severity={n.severity}
                                message={n.message}
                                onCancel={() => handleCancelNotification(n)}
                            />
                        );
                }
            });
        } else {
            return (
                <MenuItem disabled dense>
                    There doesn't seem to be anything here.
                </MenuItem>
            );
        }
    }, [notifications]);

    return (
        <React.Fragment>
            <Tooltip title="Notifications">
                <div className={IconStyles.iconButtonWrapper}>
                    <IconButton
                        id={notificationBtnId}
                        icon={
                            <NotificationIcon
                                isSelected={open}
                                hasUnreadNotifications={hasUnreadNotifications}
                            />
                        }
                        disabled={props.isSubmitting}
                        onClick={(e) => toggleMenu(e)}
                        isSelected={open}
                    />
                </div>
            </Tooltip>
            <Menu
                open={open}
                onClose={toggleMenu}
                anchorEl={document.getElementById(notificationBtnId)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {notificationsHtml}
            </Menu>
        </React.Fragment>
    );
};

export default NotificationsComponent;
